/**
 * Platform · Who can call whom.
 *
 * The administrator's rule for staff calls. It starts open — everyone may
 * call everyone, by video, in groups — and is narrowed in two ways:
 *
 *  - by access level: a four-by-four matrix of who may call whom, read as
 *    "a caller at this level can call somebody at that one", with whether
 *    each level may use video and may start group calls;
 *  - by exception, for the cases a level cannot express: this contractor
 *    may not call the chief executive; the duty manager may call anyone.
 *    An exception names a person, a role, a level or anyone at each end.
 *    A deny beats an allow, and either beats the matrix.
 *
 * v2 adds one column, "May break through Do not disturb": a level ticked there
 * may make an urgent call that rings somebody on Do not disturb. Closed for
 * every level by default — Do not disturb that anybody can ignore is not one.
 *
 * The checker answers "can X call Y?" with the reason, against the changes
 * on screen before they are saved (the same decision the platform makes —
 * domain/calls.ts `decide`) and, on the platform, against what is saved.
 *
 * Who may change it: `calls:policy`, administrators only. Everyone else sees
 * the rule read-only, because knowing why a call was refused is not a
 * privilege. The screen sits at master level in the menu.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { useCallDirectory, useCallIce, useCallPolicy, useCheckCallPolicy, useSaveCallPolicy } from "../../api/queries";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import {
  decide,
  defaultPolicy,
  LEVEL_LABEL,
  LEVELS,
  MESH_CEILING,
  rowSummary,
  validatePolicy,
  type CallPerson,
  type CallPolicy,
  type EndType,
  type Level,
  type Party,
  type PolicyEnd,
  type PolicyException,
  type PolicyRole,
} from "../../domain/calls";
import { SAMPLE_ME } from "../../mock/calls";
import { locale, t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import s from "./Calls.module.css";

const END_LABEL: Record<EndType, string> = { anyone: "Anyone", principal: "Person", role: "Role", level: "Level" };

export function CallPolicyPage() {
  const session = useSession();
  const view = useCallPolicy();
  const canEdit = session.principal.permissions.includes("calls:policy") || session.principal.permissions.includes("*");
  /* the changes on screen; null while there are none, so the saved rule shows as it is */
  const [edited, setDraft] = useState<CallPolicy | null>(null);
  const draft = edited ?? view.data?.policy ?? null;
  const save = useSaveCallPolicy();
  const [problems, setProblems] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  /* build 5: the save the changes were started from — sent with the save, so a colleague's later save is not replaced */
  const [basedOn, setBasedOn] = useState<string | null>(null);
  const conflict = save.isError && (save.error as { status?: number }).status === 409;

  const dirty = !!draft && !!view.data && JSON.stringify(draft) !== JSON.stringify(view.data.policy);
  const exceptions = draft?.exceptions.length ?? 0;
  const narrowed = draft ? LEVELS.flatMap((a) => LEVELS.filter((b) => !draft.matrix[a][b])).length : 0;

  useScreenContext({
    route: "/call-policy",
    title: "Who can call whom",
    summary: draft
      ? [
          view.data?.isDefault
            ? t("Staff calls are open: everyone may call everyone.")
            : t("{n} level pairings closed and {x} exceptions.", { n: narrowed, x: exceptions }),
        ]
      : [],
    offers: [],
    worksInFull: true,
  });

  const eyebrow = t("{site} · Platform", { site: session.site.code });
  if (view.isPending) {
    return (
      <Page eyebrow={eyebrow} title="Who can call whom">
        <Skeleton rows={8} />
      </Page>
    );
  }
  if (view.isError || !draft || !view.data) {
    return (
      <Page eyebrow={eyebrow} title="Who can call whom">
        <ErrorState error={view.error} retry={() => void view.refetch()} />
      </Page>
    );
  }

  const roles = view.data.roles;
  const edit = (fn: (p: CallPolicy) => void) => {
    if (!canEdit) return;
    if (edited === null) setBasedOn(view.data?.updatedAt ?? "default");
    const next = structuredClone(draft);
    fn(next);
    setDraft(next);
    setSaved(false);
    setProblems([]);
  };
  const onSave = () => {
    const out = validatePolicy(draft, roles.length ? roles.map((r) => r.key) : undefined);
    setProblems(out);
    if (out.length) return;
    save.mutate(
      { policy: draft, basedOn: basedOn ?? view.data?.updatedAt ?? "default" },
      {
        onSuccess: () => {
          setDraft(null);
          setBasedOn(null);
          setSaved(true);
        },
      },
    );
  };

  return (
    <Page eyebrow={eyebrow} title="Who can call whom">
      <p className={s.lede}>
        {t(
          "Set who may call whom: open by default, narrowed by level below, then exceptions.",
        )}{" "}
        <Link to="/calls">{t("Staff calls →")}</Link>
      </p>
      {!canEdit && (
        <p className={s.sample} data-testid="policy-readonly">
          {t(
            "You can view this rule but not change it. Changing it needs the calls policy permission, which administrators have.",
          )}
        </p>
      )}
      <Relay />

      <Card title="Levels" sub="Rows call columns; each direction is set separately">
        <div className={s.matrixWrap}>
          <table className={s.matrix} data-testid="policy-matrix">
            <thead>
              <tr>
                <th className={s.axis}>
                  {t("Caller")} ↓ · {t("Can call")} →
                </th>
                {LEVELS.map((l) => (
                  <th key={l}>{t(LEVEL_LABEL[l])}</th>
                ))}
                <th>{t("May use video")}</th>
                <th>{t("May start group calls")}</th>
                <th>{t("May break through Do not disturb")}</th>
              </tr>
            </thead>
            <tbody>
              {LEVELS.map((a) => (
                <tr key={a}>
                  <th scope="row">{t(LEVEL_LABEL[a])}</th>
                  {LEVELS.map((b) => (
                    <td key={b}>
                      <input
                        type="checkbox"
                        className={s.toggle}
                        checked={draft.matrix[a][b]}
                        disabled={!canEdit}
                        onChange={(e) => edit((p) => void (p.matrix[a][b] = e.target.checked))}
                        aria-label={t("{from} may call {to}", { from: t(LEVEL_LABEL[a]), to: t(LEVEL_LABEL[b]) })}
                        data-testid={`m-${a}-${b}`}
                      />
                    </td>
                  ))}
                  <td>
                    <input
                      type="checkbox"
                      className={s.toggle}
                      checked={draft.video[a]}
                      disabled={!canEdit}
                      onChange={(e) => edit((p) => void (p.video[a] = e.target.checked))}
                      aria-label={t("{level} may use video", { level: t(LEVEL_LABEL[a]) })}
                      data-testid={`video-${a}`}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      className={s.toggle}
                      checked={draft.groupStart[a]}
                      disabled={!canEdit}
                      onChange={(e) => edit((p) => void (p.groupStart[a] = e.target.checked))}
                      aria-label={t("{level} may start group calls", { level: t(LEVEL_LABEL[a]) })}
                      data-testid={`group-${a}`}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      className={s.toggle}
                      checked={!!draft.dndOverride?.[a]}
                      disabled={!canEdit}
                      onChange={(e) => edit((p) => void (p.dndOverride = { ...p.dndOverride, [a]: e.target.checked }))}
                      aria-label={t("{level} may break through Do not disturb", { level: t(LEVEL_LABEL[a]) })}
                      data-testid={`dnd-override-${a}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.rows} data-testid="policy-rows">
          {LEVELS.map((l) => {
            const r = rowSummary(draft, l);
            const vars = { from: t(LEVEL_LABEL[l]) };
            return (
              <span key={l}>
                {t(r.template, vars)}
                {r.template === "{from} may call only:" && <> {r.levels.map((x) => t(LEVEL_LABEL[x])).join(", ")}.</>}
                {!draft.video[l] && <> {t("Voice only.")}</>}
                {!draft.groupStart[l] && <> {t("No group calls.")}</>}
                {draft.dndOverride?.[l] && <> {t("Urgent calls ring through Do not disturb.")}</>}
              </span>
            );
          })}
        </div>
      </Card>

      <Card title="Group size and ringing">
        <div className={s.formRow}>
          <Field label="Most people on one call">
            <Input
              type="number"
              min={2}
              max={MESH_CEILING}
              className={s.small}
              value={draft.groupMax}
              disabled={!canEdit}
              onChange={(e) => edit((p) => void (p.groupMax = Number(e.target.value)))}
              data-testid="group-max"
            />
          </Field>
          <Field label="Ring for (seconds)">
            <Input
              type="number"
              min={15}
              max={120}
              className={s.small}
              value={draft.ringSeconds}
              disabled={!canEdit}
              onChange={(e) => edit((p) => void (p.ringSeconds = Number(e.target.value)))}
              data-testid="ring-seconds"
            />
          </Field>
        </div>
        <p className={s.note}>
          {t(
            "Between 2 and 8 people. Larger calls would stutter and need a media server, which isn't installed. An unanswered ring is logged as missed after the ring time.",
          )}
        </p>
        <p className={s.note}>
          {t(
            "In a group call, whoever invites someone must be allowed to call them. Others on the call don't need to be.",
          )}
        </p>
        <p className={s.note}>
          {t(
            "Do not disturb stops calls ringing for a while. The person’s own list still rings through. Urgent calls ring through only from levels ticked under “May break through Do not disturb” (none by default). The host sets who can join a group call and its lobby, but nobody joins who may not call the host.",
          )}
        </p>
      </Card>

      <Exceptions draft={draft} roles={roles} canEdit={canEdit} edit={edit} />

      <Checker draft={draft} />

      {canEdit && (
        <div className={s.saveBar}>
          <Button variant="primary" onClick={onSave} disabled={!dirty || save.isPending} data-testid="policy-save">
            {save.isPending ? t("Saving…") : t("Save")}
          </Button>
          <Button onClick={() => edit((p) => Object.assign(p, defaultPolicy()))} data-testid="policy-default">
            {t("Restore the open default")}
          </Button>
          {dirty && <span className={s.dim}>{t("Changes not saved yet.")}</span>}
          {saved && !dirty && (
            <span className={s.dim} data-testid="policy-saved">
              {t("Saved. Calls follow the new rule from now.")}
            </span>
          )}
          {save.isError && !conflict && <span className={s.refused}>{(save.error as Error).message}</span>}
          {conflict && (
            <span className={s.refused} role="alert" data-testid="policy-conflict">
              {t("Someone else saved this rule after you opened it. Your changes were not saved.")}{" "}
              <Button
                onClick={() => {
                  setDraft(null);
                  setBasedOn(null);
                  save.reset();
                  void view.refetch();
                }}
                data-testid="policy-theirs"
              >
                {t("Show their version")}
              </Button>
            </span>
          )}
          {view.data.updatedAt && !dirty && (
            <span className={s.dim} data-testid="policy-stamp">
              {t("Last saved by {who}, {when}.", {
                who: view.data.updatedBy ?? t("someone"),
                when: new Date(view.data.updatedAt).toLocaleString(locale()),
              })}
            </span>
          )}
        </div>
      )}
      {problems.length > 0 && (
        <ul className={s.problems} role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </Page>
  );
}

/* ── the relay ────────────────────────────────────────────────────────── */

function Relay() {
  const ice = useCallIce();
  if (!ice.data) return null;
  return ice.data.relay ? (
    <p className={s.relay} data-ok="true" data-testid="relay">
      {t("A relay (TURN) server is set up, so calls connect across networks, including phones on mobile data.")}
    </p>
  ) : (
    <p className={s.relay} data-ok="false" data-testid="relay">
      {t(
        "No relay (TURN) server is set up, so calls across networks, like a phone on mobile data, may not connect. Ask IT to set SLT_TURN_URLS and SLT_TURN_SECRET.",
      )}
    </p>
  );
}

/* ── exceptions ───────────────────────────────────────────────────────── */

function useEndLabel(roles: PolicyRole[]) {
  const dir = useCallDirectory();
  const people = dir.data?.items ?? [];
  return (end: PolicyEnd): string => {
    switch (end.type) {
      case "anyone":
        return t("Anyone");
      case "level":
        return t("anyone at {level}", { level: t(LEVEL_LABEL[(end.id as Level) ?? "operations"] ?? end.id ?? "") });
      case "role":
        return t("role: {role}", { role: roles.find((r) => r.key === end.id)?.name ?? end.id ?? "" });
      case "principal":
        return end.id === SAMPLE_ME ? t("You") : (people.find((p) => p.principalId === end.id)?.name ?? end.id ?? "");
    }
  };
}

function EndPicker({
  value,
  onChange,
  roles,
  testId,
}: {
  value: PolicyEnd;
  onChange: (e: PolicyEnd) => void;
  roles: PolicyRole[];
  testId: string;
}) {
  const dir = useCallDirectory();
  const people = dir.data?.items ?? [];
  return (
    <span className={s.formRow}>
      <Select
        value={value.type}
        onChange={(e) => onChange({ type: e.target.value as EndType, id: null })}
        aria-label={t("Kind")}
        data-testid={`${testId}-type`}
      >
        {(Object.keys(END_LABEL) as EndType[]).map((k) => (
          <option key={k} value={k}>
            {t(END_LABEL[k])}
          </option>
        ))}
      </Select>
      {value.type !== "anyone" && (
        <Select
          value={value.id ?? ""}
          onChange={(e) => onChange({ ...value, id: e.target.value || null })}
          aria-label={t(END_LABEL[value.type])}
          data-testid={`${testId}-id`}
        >
          <option value="">{t("Choose…")}</option>
          {value.type === "principal" &&
            people.map((p) => (
              <option key={p.principalId} value={p.principalId}>
                {p.name}
              </option>
            ))}
          {value.type === "role" &&
            roles.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name}
              </option>
            ))}
          {value.type === "level" &&
            LEVELS.map((l) => (
              <option key={l} value={l}>
                {t(LEVEL_LABEL[l])}
              </option>
            ))}
        </Select>
      )}
    </span>
  );
}

function Exceptions({
  draft,
  roles,
  canEdit,
  edit,
}: {
  draft: CallPolicy;
  roles: PolicyRole[];
  canEdit: boolean;
  edit: (fn: (p: CallPolicy) => void) => void;
}) {
  const label = useEndLabel(roles);
  const blank = (): PolicyException => ({
    id: `x-${Date.now().toString(36)}`,
    from: { type: "anyone", id: null },
    to: { type: "principal", id: null },
    effect: "deny",
    note: "",
  });
  const [x, setX] = useState<PolicyException>(blank);
  const ready = (e: PolicyEnd) => e.type === "anyone" || !!e.id;
  return (
    <Card title="Exceptions" sub="Checked before the levels. A deny beats an allow.">
      {draft.exceptions.length === 0 ? (
        <p className={s.dim}>{t("No exceptions: the levels decide every call.")}</p>
      ) : (
        <ul className={s.exceptions} data-testid="exceptions">
          {draft.exceptions.map((e) => (
            <li key={e.id}>
              <span className={e.effect === "allow" ? s.allow : s.deny}>
                {e.effect === "allow" ? t("Allow") : t("Deny")}
              </span>
              <span>{t("from {from} to {to}", { from: label(e.from), to: label(e.to) })}</span>
              {e.note && <span className={s.dim}>— {e.note}</span>}
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => edit((p) => void (p.exceptions = p.exceptions.filter((y) => y.id !== e.id)))}
                >
                  {t("Remove")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <div className={s.formRow} data-testid="exception-form">
          <Field label="From">
            <EndPicker value={x.from} onChange={(from) => setX({ ...x, from })} roles={roles} testId="x-from" />
          </Field>
          <Field label="To">
            <EndPicker value={x.to} onChange={(to) => setX({ ...x, to })} roles={roles} testId="x-to" />
          </Field>
          <Field label="Effect">
            <Select
              value={x.effect}
              onChange={(e) => setX({ ...x, effect: e.target.value as "allow" | "deny" })}
              data-testid="x-effect"
            >
              <option value="deny">{t("Deny")}</option>
              <option value="allow">{t("Allow")}</option>
            </Select>
          </Field>
          <Field label="Note">
            <Input
              value={x.note}
              onChange={(e) => setX({ ...x, note: e.target.value })}
              placeholder={t("why — shown to the person refused")}
              data-testid="x-note"
            />
          </Field>
          <Button
            disabled={!ready(x.from) || !ready(x.to)}
            onClick={() => {
              edit((p) => void p.exceptions.push(x));
              setX(blank());
            }}
            data-testid="x-add"
          >
            {t("Add the exception")}
          </Button>
        </div>
      )}
    </Card>
  );
}

/* ── the checker ──────────────────────────────────────────────────────── */

function Checker({ draft }: { draft: CallPolicy }) {
  const session = useSession();
  const dir = useCallDirectory();
  const check = useCheckCallPolicy();
  const sample = session.source.kind === "mock";
  const people: CallPerson[] = useMemo(() => dir.data?.items ?? [], [dir.data]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  /* the sample can test its own sign-in; the platform's directory leaves the person looking out */
  const options = sample ? [{ principalId: SAMPLE_ME, name: t("You") }, ...people] : people;
  const party = (id: string): Party | null => {
    if (sample && id === SAMPLE_ME) return { principalId: SAMPLE_ME, level: "master", roles: ["administrator"] };
    const p = people.find((x) => x.principalId === id);
    return p ? { principalId: p.principalId, level: p.level, roles: p.roles } : null;
  };
  const a = party(from);
  const b = party(to);
  const local = a && b ? decide(a, b, draft) : null;

  return (
    <Card title="Can one person call another?" sub="The answer, and the rule that decides it">
      <div className={s.formRow}>
        <Field label="Caller">
          <Select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="check-from">
            <option value="">{t("Choose…")}</option>
            {options.map((p) => (
              <option key={p.principalId} value={p.principalId}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Calls">
          <Select value={to} onChange={(e) => setTo(e.target.value)} data-testid="check-to">
            <option value="">{t("Choose…")}</option>
            {options.map((p) => (
              <option key={p.principalId} value={p.principalId}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        {!sample && (
          <Button
            disabled={!from || !to || check.isPending}
            onClick={() => check.mutate({ from, to })}
            data-testid="check-saved"
          >
            {t("Ask the platform (saved rule)")}
          </Button>
        )}
      </div>
      {local && (
        <p className={s.answer} data-allowed={local.allowed} data-testid="check-answer">
          <b>{local.allowed ? t("Allowed.") : t("Refused.")}</b> {t(local.template, translateVars(local.vars))}{" "}
          {local.allowed && !local.video && t("By voice only — the caller's level has no video.")}
        </p>
      )}
      {check.data && (
        <p className={s.answer} data-allowed={check.data.allowed}>
          <b>{t("Saved rule:")}</b> {check.data.allowed ? t("Allowed.") : t("Refused.")} {check.data.reason}
        </p>
      )}
      {check.isError && <p className={s.refused}>{(check.error as Error).message}</p>}
    </Card>
  );
}

/** Level names inside a reason are translated; a note stays as the administrator wrote it. */
function translateVars(vars: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, k === "note" ? v : t(v)]));
}
